import { memo, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiActions,
  MiInternalDrawer,
  MiLoader,
  MiModalConfirm,
  MiTab,
} from "@miplatform/components";
import {
  ACTIONS_LIST_TYPE,
  ATTEMPT_DERIVED_STATUS,
  ATTEMPT_STATUS_COLORS,
  BUTTON_COLORS,
  EMAIL_TYPE,
} from "@miplatform/constants";
import { getDurationInHours } from "@miplatform/utils";
import { mdiArrowCollapse, mdiClose, mdiPrinterOutline } from "@mdi/js";
import { Box, useMediaQuery } from "@mui/material";
import { CloseoutDrawerHeader } from "./CloseoutDrawerHeader/CloseoutDrawerHeader";
import { CloseoutDrawerSubHeader } from "./CloseoutDrawerSubHeader/CloseoutDrawerSubHeader";
import { IssueList } from "./IssueList/IssueList";
import { AttemptHistory } from "./Tabs/AttemptHistory/AttemptHistory";
import { CloseoutActivity } from "./Tabs/CloseoutActivity/CloseoutActivity";
import { CloseoutContacts } from "./Tabs/CloseoutContacts/CloseoutContacts";
import { CloseoutDetails } from "./Tabs/CloseoutDetails/CloseoutDetails";
import { CloseoutDocuments } from "./Tabs/CloseoutDocuments/CloseoutDocuments";
import { AssignmentReview } from "./Tabs/Issues/AssignmentReview/AssignmentReview";
import { CloseoutPrebill } from "./Tabs/Issues/CloseoutPrebill/CloseoutPrebill";
import { Closingout } from "./Tabs/Issues/Closingout/Closingout";
import { Review } from "./Tabs/Issues/Closingout/Review";
import {
  CLOSEOUT_TAB,
  CLOSEOUT_TABS,
  MINIMIZE_CLOSEOUT_DRAWER_CONFIG,
  CLOSEOUT_SCREENS as SCREENS,
} from "./constants";
import { useCloseoutDrawerModel } from "./useCloseoutDrawerModel";

export const CloseoutDrawer = memo(
  ({
    open = false,
    wasMinimized = false,
    isCloseoutMinimized = false,
    isPreBillMinimized = false,
    listAttempt = {},
    tab,
    screen,
    view,
    onClose = () => null,
    onMinimize = () => null,
    onAttemptUpdated = () => null,
    onEmailDrawerOpen = () => null,
  }) => {
    const {
      stateManager,
      isLoading,
      hasPermission,
      attempt: mappedAttempt,
      attemptHome,
      documents,
      attemptUser,
      attemptActivities,
      orderTemplate,
      loading,
      nextAttempt,
      getAttempt,
      getOrder,
      getHome,
      getOrderTemplate,
      getUser,
      getTenantAccount,
      getAttemptActivities,
      closeoutAttempt,
      inProgressAttempt,
      handlePrint,
      setAttempt,
      getRelatedUsers,
      users,
      updateOrder,
    } = useCloseoutDrawerModel({
      attemptId: listAttempt.id,
      orderId: listAttempt.orderId,
      onAttemptUpdated,
    });

    const tabs = useMemo(() => {
      return CLOSEOUT_TABS.filter((tab) => {
        if (tab.permission) {
          return hasPermission(tab.permission);
        }
        return true;
      });
    }, []);

    const {
      attemptStatus,
      assignment: {
        order: { orderTemplateColor: assignmentTemplateColor } = {},
      } = {},
      duration,
      exactExtendedTime,
    } = listAttempt;
    const attempt = mappedAttempt.id ? mappedAttempt : listAttempt;

    const [activeTab, setActiveTab] = useState(tab || CLOSEOUT_TAB.TASKS);
    const [activeScreen, setActiveScreen] = useState(screen || SCREENS.MAIN);
    const [isWoReviewed, setIsWoReviewed] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [issueIndex, setIssueIndex] = useState(0);
    const { title, description } =
      MINIMIZE_CLOSEOUT_DRAWER_CONFIG[activeScreen] ||
      MINIMIZE_CLOSEOUT_DRAWER_CONFIG.default;
    const hideAddNewIssueButton = !orderTemplate?.fields?.find(
      (field) => field.fieldName === "issue"
    )?.visible;

    const diffInDuration =
      exactExtendedTime ||
      (attempt.endDate
        ? getDurationInHours(attempt.startDate, attempt.endDate) -
          getDurationInHours(
            attempt.scheduledStartDate,
            attempt.scheduledEndDate
          )
        : 0);

    useEffect(() => {
      getTenantAccount();
    }, []);

    useEffect(() => {
      if (tab) {
        setActiveTab(tab);
      }
    }, [tab]);

    // Once per attempt we are going to fetch all the related users
    useEffect(() => {
      if (mappedAttempt) {
        getRelatedUsers(mappedAttempt);
      }
    }, [mappedAttempt.id]);

    useEffect(() => {
      if (!open && !wasMinimized) {
        setIsWoReviewed(false);
        setActiveScreen(SCREENS.MAIN);
      }
    }, [open, wasMinimized]);

    useEffect(() => {
      if (open && listAttempt) {
        getAttempt();
        getOrder();
        getOrderTemplate(listAttempt.assignment?.order?.orderTemplateId);
      }
    }, [
      open,
      wasMinimized,
      screen,
      listAttempt,
      attempt.orderTypeId,
      attemptStatus,
    ]);

    useEffect(() => {
      if (open && attempt.home) {
        getHome(attempt.home?.id);
      }
      if (open && attempt.home) {
        getHome(attempt.home?.id);
      }
      if (open && attempt.assignedUserId) {
        getUser(attempt.assignedUserId);
      }
    }, [open, attempt.home, attempt.assignedUserId, listAttempt]);

    useEffect(() => {
      if (
        open &&
        [
          SCREENS.MAIN,
          SCREENS.PREBILL,
          SCREENS.REVIEW,
          SCREENS.WO_REVIEW,
        ].includes(activeScreen)
      ) {
        getAttempt();
      }
    }, [open, activeScreen]);

    useEffect(() => {
      if ([SCREENS.PREBILL, SCREENS.CLOSEOUT].includes(activeScreen)) {
        setIssueIndex(0);
      }
    }, [activeScreen]);

    const { classes, theme } = useStyles({
      statusColor: ATTEMPT_STATUS_COLORS[attempt.attemptStatus],
    });
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

    const {
      home = {},
      orderRequestDate,
      orderPriority,
      orderTimePreference,
    } = attempt;
    const { address = {} } = home;

    const handleClosing = () => {
      setActiveScreen(SCREENS.MAIN);
      setActiveTab(CLOSEOUT_TAB.TASKS);
      setAttempt({});
      onClose(listAttempt.id);
    };

    const handleConfirmModalOpen = () => setOpenConfirmModal(true);

    const handleConfirmModalClose = () => setOpenConfirmModal(false);

    const actions = [
      {
        name: "Print",
        icon: mdiPrinterOutline,
        disabled: isLoading,
        onClick: handlePrint,
      },
      {
        name: "Minimize",
        icon: mdiArrowCollapse,
        disabled:
          (activeScreen !== SCREENS.CLOSEOUT &&
            activeScreen !== SCREENS.PREBILL) ||
          (attempt.derivedStatus === ATTEMPT_DERIVED_STATUS.PENDING_CLOSEOUT &&
            activeScreen === SCREENS.CLOSEOUT &&
            isCloseoutMinimized) ||
          (attempt.derivedStatus === ATTEMPT_DERIVED_STATUS.PENDING_PREBILL &&
            activeScreen === SCREENS.PREBILL &&
            isPreBillMinimized),
        onClick: [
          ATTEMPT_DERIVED_STATUS.PENDING_PREBILL,
          ATTEMPT_DERIVED_STATUS.PENDING_CLOSEOUT,
        ].includes(attempt.derivedStatus)
          ? handleConfirmModalOpen
          : () => onMinimize(listAttempt.id),
      },
      {
        name: "Close",
        icon: mdiClose,
        onClick: handleClosing,
      },
    ];

    const handleCloseout = () => {
      closeoutAttempt();
      if (!isWoReviewed) {
        handleClosing();
      }
      setActiveScreen(isWoReviewed ? SCREENS.WO_REVIEW : SCREENS.MAIN);
    };

    const handleCancel = () => {
      setActiveScreen(SCREENS.MAIN);
      setSelectedIssue(null);
    };

    const handleEmailDrawerOpen = () => {
      onEmailDrawerOpen({ attempt, type: EMAIL_TYPE.CLOSEOUT_AND_UPDATE });
    };

    return (
      <MiInternalDrawer
        open={open}
        unmountOnExit={false}
        className={classes.drawer}
        width={isSmallScreen ? 440 : 820}
        title={address.addressLine1}
        subtitle={[
          address && `${address.city}, ${address.regionCode}`,
          home.communityName,
          home.builderName,
        ]}
        rightAction={
          <MiActions
            type={ACTIONS_LIST_TYPE.ICONS}
            actions={actions}
            buttonsProps={{ sx: { padding: 1 } }}
          />
        }
      >
        <Box className={classes.container}>
          <CloseoutDrawerHeader
            assignment={attempt?.assignment}
            attemptNumber={attempt?.attemptNumber}
            user={attemptUser}
            contacts={attemptHome?.contacts}
            isATakeover={home.isATakeover}
            activeScreen={activeScreen}
            issueIndex={issueIndex}
            issuesLength={attempt.issues?.length || 1}
            unfilteredIssuesLength={attempt.unfilteredIssues?.length || 1}
          />
          <CloseoutDrawerSubHeader
            scheduledDate={attempt.scheduledStartDate}
            createdOn={attempt.createdOn}
            derivedStatus={attempt.derivedStatus}
            jobNumber={attempt.jobNumber}
            duration={duration + exactExtendedTime}
            diffInDuration={diffInDuration}
            isExtended={diffInDuration > 0}
            issues={attempt.issues}
            unfilteredIssues={attempt.unfilteredIssues}
            activeScreen={activeScreen}
          />
          {activeScreen === SCREENS.MAIN && (
            <Box className={classes.screen}>
              <Box className={classes.tabContainer}>
                <MiTab
                  isSecondary
                  tabs={tabs}
                  currentTab={activeTab}
                  onTabChange={setActiveTab}
                />
              </Box>
              <MiLoader type="linear" isLoading={loading} />
              {activeTab === CLOSEOUT_TAB.DOCUMENTS && (
                <CloseoutDocuments
                  attempt={listAttempt}
                  isLoading={loading}
                  orderTemplate={orderTemplate}
                  home={attemptHome}
                  documents={documents}
                  getHome={getHome}
                  getOrderTemplate={getOrderTemplate}
                  getOrder={getOrder}
                />
              )}
              {activeTab === CLOSEOUT_TAB.TASKS && (
                <IssueList
                  isViewable
                  isLoading={isLoading}
                  hideAddNewIssueButton={hideAddNewIssueButton}
                  attempt={{
                    ...attempt,
                    assignment: {
                      ...attempt.assignment,
                      assignmentTemplateColor,
                    },
                  }}
                  users={users}
                  getAttempt={getAttempt}
                  setActiveScreen={setActiveScreen}
                  setSelectedIssue={setSelectedIssue}
                  closeoutAttempt={closeoutAttempt}
                  inProgressAttempt={inProgressAttempt}
                />
              )}
              {activeTab === CLOSEOUT_TAB.DETAILS && (
                <CloseoutDetails
                  home={attemptHome}
                  orderId={attempt.orderId}
                  updateOrder={updateOrder}
                  orderModifiedOn={attempt.orderModifiedOn}
                  orderNotes={attempt.orderNotes}
                  orderInfo={{
                    orderRequestDate,
                    orderPriority,
                    orderTimePreference,
                  }}
                />
              )}
              {activeTab === CLOSEOUT_TAB.HISTORY && (
                <AttemptHistory
                  open={open}
                  users={users}
                  assignmentId={attempt?.assignment?.id}
                  currentAttemptId={attempt?.id}
                  stateManager={stateManager}
                />
              )}
              {activeTab === CLOSEOUT_TAB.ACTIVITY && (
                <CloseoutActivity
                  open={open}
                  attemptModifiedOn={listAttempt?.modifiedOn}
                  activities={attemptActivities}
                  getAttemptActivities={getAttemptActivities}
                />
              )}
              {activeTab === CLOSEOUT_TAB.CONTACTS && (
                <CloseoutContacts
                  home={attemptHome}
                  onRefresh={() => getHome(attempt.home?.id)}
                />
              )}
            </Box>
          )}
          {activeScreen === SCREENS.CLOSEOUT && (
            <Closingout
              attempt={attempt}
              view={view}
              issueIndex={issueIndex}
              setIssueIndex={setIssueIndex}
              getAttempt={getAttempt}
              closeoutAttempt={closeoutAttempt}
              setActiveScreen={setActiveScreen}
              onCancel={handleCancel}
              setIsWoReviewed={setIsWoReviewed}
              selectedIssue={selectedIssue}
              setSelectedIssue={setSelectedIssue}
              users={users}
              isLoading={isLoading}
            />
          )}
          {activeScreen === SCREENS.PREBILL && (
            <CloseoutPrebill
              stateManager={stateManager}
              attempt={attempt}
              users={users}
              isLoading={isLoading}
              issueIndex={issueIndex}
              setIssueIndex={setIssueIndex}
              getAttempt={getAttempt}
              onCancel={handleCancel}
            />
          )}
          {activeScreen === SCREENS.REVIEW && (
            <Review
              attempt={attempt}
              onCancel={handleCancel}
              closeoutAttempt={handleCloseout}
              isWoReviewed={isWoReviewed}
              onEmailDrawerOpen={handleEmailDrawerOpen}
            />
          )}
          {activeScreen === SCREENS.WO_REVIEW && nextAttempt !== null && (
            <AssignmentReview attempt={nextAttempt} onClose={handleClosing} />
          )}
        </Box>
        <MiModalConfirm
          title={title}
          titleIcon={{
            path: mdiArrowCollapse,
            color: theme.palette.primary.main,
          }}
          textPositive="Minimize"
          open={openConfirmModal}
          handleNegative={handleConfirmModalClose}
          handlePositive={() => {
            onMinimize(listAttempt.id, attempt.derivedStatus);
            handleConfirmModalClose();
          }}
          description={description}
          buttonPositiveColor={BUTTON_COLORS.PRIMARY}
        />
      </MiInternalDrawer>
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  drawer: {
    borderLeft: `${theme.spacing(2)} solid ${theme.palette.grey.accent}`,
    boxSizing: "content-box",
    overflow: "auto",
  },
  container: {
    margin: `${theme.spacing(1)} ${theme.spacing(4)} 0`,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%",
  },
  screen: {
    height: "100%",
    overflow: "auto",
  },
  tabContainer: {
    borderBottom: `1px solid ${theme.palette.grey.medium}`,
    zIndex: theme.zIndex.appBar,
    position: "sticky",
    top: 0,
  },
  subtitleContainer: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  takeoverBadge: {
    height: theme.spacing(5),
    alignItems: "center",
    marginLeft: theme.spacing(1),
  },
}));
